import {
  BRAND_DARK,
  BRAND_FONT_TEXT,
  BRAND_SHAPE,
  BRAND_STAGE_DARK,
  BRAND_STAGE_LIGHT,
} from "./brand-tokens";

/**
 * Brand values for maps (AD #694). Map colours are set in JavaScript (Mapbox
 * paint properties, DOM marker styles, SVG strings, static-image URLs), where
 * the `brand.css` scope can't reach, so maps read them from here. Everything
 * derives from `brand-tokens.ts`; no new literals.
 *
 * Roles: pins, markers and route lines are fills (#C20000). Hover and
 * selection rings and small dots are the accent (#FF4D4D). Depth is a black
 * shadow, never a red glow.
 */

/** Pin, marker and route-line fill. White on it is 6.4:1 (stop numbers). */
export const MAP_PIN = BRAND_DARK.redFill;
/** Hover / selected ring, small dots, outlines on imagery. */
export const MAP_ACCENT = BRAND_DARK.redAccent;
/** Mapbox Static Images pin colour (`pin-l+C20000`): hex without `#`. */
export const MAP_PIN_STATIC = MAP_PIN.slice(1).toUpperCase();
/** Google Static Maps marker colour (`color:0xC20000`). */
export const MAP_PIN_GOOGLE = `0x${MAP_PIN_STATIC}`;

/** Earlier Field visits still open — the amber "pending" hue (AD #788). */
export const MAP_PIN_OUTSTANDING = BRAND_STAGE_DARK.edit;

/**
 * Program hues for layered Field maps. Never red: red is today's route and
 * the action colour. Cycles when a Spatialist carries more programs.
 */
export const MAP_PROGRAM_PALETTE: readonly string[] = [
  BRAND_STAGE_DARK.qa,
  BRAND_STAGE_DARK.incoming,
  BRAND_STAGE_DARK.deliver,
  BRAND_STAGE_DARK.field,
  BRAND_STAGE_LIGHT.qa,
  BRAND_STAGE_LIGHT.incoming,
  BRAND_STAGE_LIGHT.deliver,
];

/** Resting marker depth (replaces red glows). */
export const MAP_MARKER_SHADOW = "0 2px 6px rgba(0,0,0,0.45)";
/** Hovered / selected marker depth (replaces the stronger red glow). */
export const MAP_MARKER_SHADOW_ACTIVE = "0 4px 12px rgba(0,0,0,0.6)";

/**
 * Map popups sit on dark basemaps and imagery. They use the raised tile, not
 * the floating layer: small red text (#FF4D4D) reaches 4.7:1 on #232429 but
 * only 4.3:1 on #2A2C31.
 */
export const MAP_POPUP = {
  bg: BRAND_DARK.tileRaised,
  text: BRAND_DARK.text,
  textSecondary: BRAND_DARK.textSecondary,
  textTertiary: BRAND_DARK.textTertiary,
  line: BRAND_DARK.line,
  lineStrong: BRAND_DARK.lineStrong,
  /** Small red text and links (5.1:1 on the canvas, 4.7:1 on the popup). */
  redText: BRAND_DARK.redText,
  /** Red text on a red-tinted badge (the tint lowers the ground). */
  redBadgeText: BRAND_DARK.redTextHover,
  /** Primary action fill; white label at 6.4:1. */
  redFill: BRAND_DARK.redFill,
  radius: BRAND_SHAPE.radius,
  buttonRadius: BRAND_SHAPE.radiusButton,
  /** next/font hashes the Hind family name, so the variable comes first. */
  font: `var(--font-brand-text), ${BRAND_FONT_TEXT}`,
} as const;

/** A tinted badge ground: the fill at `alpha` over the popup surface (hex). */
export function mapPopupTint(alpha = 0.2): string {
  const a = [1, 3, 5].map((i) => parseInt(MAP_POPUP.redFill.slice(i, i + 2), 16));
  const b = [1, 3, 5].map((i) => parseInt(MAP_POPUP.bg.slice(i, i + 2), 16));
  const mix = a.map((v, i) => Math.round(v * alpha + b[i] * (1 - alpha)));
  return `#${mix.map((v) => v.toString(16).padStart(2, "0")).join("")}`.toUpperCase();
}

/**
 * The shared popup stylesheet for Mapbox popups carrying `className`
 * (missions map and locations map both use `vs-mission-popup`).
 */
export function mapPopupCss(className = "vs-mission-popup"): string {
  const p = `.mapboxgl-popup.${className}`;
  return `
${p} .mapboxgl-popup-content {
  background: ${MAP_POPUP.bg}; color: ${MAP_POPUP.text}; border: 1px solid ${MAP_POPUP.line};
  border-radius: ${MAP_POPUP.radius}; padding: 12px 14px 10px;
  box-shadow: ${BRAND_SHAPE.shadowLift}, ${BRAND_SHAPE.shadowRest};
  font-family: ${MAP_POPUP.font};
}
${p}.mapboxgl-popup-anchor-top .mapboxgl-popup-tip,
${p}.mapboxgl-popup-anchor-top-left .mapboxgl-popup-tip,
${p}.mapboxgl-popup-anchor-top-right .mapboxgl-popup-tip { border-bottom-color: ${MAP_POPUP.bg}; }
${p}.mapboxgl-popup-anchor-bottom .mapboxgl-popup-tip,
${p}.mapboxgl-popup-anchor-bottom-left .mapboxgl-popup-tip,
${p}.mapboxgl-popup-anchor-bottom-right .mapboxgl-popup-tip { border-top-color: ${MAP_POPUP.bg}; }
${p}.mapboxgl-popup-anchor-left .mapboxgl-popup-tip { border-right-color: ${MAP_POPUP.bg}; }
${p}.mapboxgl-popup-anchor-right .mapboxgl-popup-tip { border-left-color: ${MAP_POPUP.bg}; }
${p} .mapboxgl-popup-close-button { color: ${MAP_POPUP.textSecondary}; font-size: 18px; padding: 4px 8px; right: 2px; top: 0; background: transparent; }
${p} .mapboxgl-popup-close-button:hover { background: rgba(255,255,255,0.06); color: ${MAP_POPUP.text}; }
`;
}
